import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Download, ExternalLink, FileText, Loader2 } from "lucide-react";

export interface VerificationDoc {
  key: string;
  label: string;
  name: string;
  url: string;
}

const isImage = (name: string) => /\.(png|jpe?g|gif|webp|avif)$/i.test(name);

/** Resolves a stored value that may be a signed URL (possibly expired) or a raw storage path. */
const resolveUrl = async (raw: string) => {
  const signMatch = raw.match(/\/storage\/v1\/object\/sign\/([^/]+)\/([^?]+)/);
  if (signMatch) {
    const { data } = await supabase.storage
      .from(signMatch[1])
      .createSignedUrl(decodeURIComponent(signMatch[2]), 60 * 60);
    return data?.signedUrl ?? "";
  }
  if (/^https?:\/\//i.test(raw)) return raw;
  const { data } = await supabase.storage.from("verification-documents").createSignedUrl(raw, 60 * 60);
  return data?.signedUrl ?? "";
};

const DocumentViewerDialog = ({
  doc,
  open,
  onOpenChange,
}: {
  doc: VerificationDoc | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const [url, setUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!doc || !open) {
      setUrl("");
      return;
    }
    setLoading(true);
    resolveUrl(doc.url).then((u) => {
      if (active) {
        setUrl(u);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [doc, open]);

  const image = useMemo(() => (doc ? isImage(doc.name) || isImage(doc.url.split("?")[0]) : false), [doc]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-zinc-950 border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">{doc?.label ?? "Document"}</DialogTitle>
          <p className="text-xs text-gray-400 truncate">{doc?.name}</p>
        </DialogHeader>

        <div className="h-[65vh] rounded-xl border border-zinc-800 bg-black overflow-hidden flex items-center justify-center">
          {loading || !url ? (
            <div className="flex flex-col items-center text-gray-400">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <FileText className="h-8 w-8" />}
              <p className="text-sm mt-2">{loading ? "Opening document…" : "Document unavailable"}</p>
            </div>
          ) : image ? (
            <img src={url} alt={doc?.label ?? "Verification document"} className="max-h-full max-w-full object-contain" />
          ) : (
            <iframe src={url} title={doc?.label ?? "Document"} className="w-full h-full bg-white text-black" />
          )}
        </div>

        {url && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="border-zinc-700" asChild>
              <a href={url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" /> Open in new tab
              </a>
            </Button>
            <Button asChild>
              <a href={url} download={doc?.name}>
                <Download className="h-4 w-4 mr-2" /> Download
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewerDialog;
